import { createContext, useContext, useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('admin_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    
    const verifyToken = async () => {
      if (!token) {
        if (isMounted) setLoading(false);
        return;
      }

      try {
        const res = await fetch(`${API_URL}/api/admin/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (!isMounted) return;
        
        if (res.ok) {
          const cloned = res.clone();
          const data = await cloned.json();
          setUser(data);
        } else {
          localStorage.removeItem('admin_token');
          setToken(null);
          setUser(null);
        }
      } catch (err) {
        if (isMounted) {
          localStorage.removeItem('admin_token');
          setToken(null);
          setUser(null);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    verifyToken();
    
    return () => { isMounted = false; };
  }, [token]);

  const login = async (email, password) => {
    const res = await fetch(`${API_URL}/api/admin/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });

    // Clone response before reading to prevent "body already read" errors
    const cloned = res.clone();
    
    let data;
    try {
      data = await cloned.json();
    } catch (e) {
      throw new Error('Server error - please try again');
    }
    
    if (!res.ok) {
      throw new Error(data.detail || 'Invalid email or password');
    }

    localStorage.setItem('admin_token', data.access_token);
    setToken(data.access_token);
    setUser(data.user);
    return data;
  };

  const logout = () => {
    localStorage.removeItem('admin_token');
    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user,
    login,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
